// Vote optimiste avec retour arrière en cas d'échec (porté de FeedbackVote).
import type { JSX } from 'preact'
import { useState } from 'preact/hooks'
import type { FeedbackItem } from '@feedbaacks/sdk'
import { useFeedbaacks } from '../context'
import { ChevronDown, ChevronUp } from '../icons'
import { cx } from '../util'

/** L'item tel qu'il sera après le vote (calcul pur, testé). */
export function applyVote(item: FeedbackItem, next: -1 | 0 | 1): FeedbackItem {
  const prev = item.current_user_vote ?? 0
  const up = item.votes_up - (prev === 1 ? 1 : 0) + (next === 1 ? 1 : 0)
  const down = item.votes_down - (prev === -1 ? 1 : 0) + (next === -1 ? 1 : 0)
  return { ...item, votes_up: up, votes_down: down, votes_score: up - down, current_user_vote: next === 0 ? null : next }
}

export function Vote({ item, onChange }: { item: FeedbackItem; onChange: (next: FeedbackItem) => void }): JSX.Element {
  const { client, t, notify } = useFeedbaacks()
  const [busy, setBusy] = useState(false)
  const [shaking, setShaking] = useState(false)
  const isUp = item.current_user_vote === 1
  const isDown = item.current_user_vote === -1

  const click = async (target: 1 | -1) => {
    if (busy) return
    const next = item.current_user_vote === target ? 0 : target
    const prev = item
    onChange(applyVote(item, next))
    setBusy(true)
    try {
      const r = await client.feedback.vote(item.id, next)
      onChange({ ...applyVote(prev, next), votes_up: r.votes_up, votes_down: r.votes_down, votes_score: r.votes_score })
    } catch {
      setShaking(true)
      setTimeout(() => setShaking(false), 220)
      onChange(prev)
      notify('error', t('vote_failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div class={cx('fb-vote', shaking && 'fb-shake')} onClick={(e) => e.stopPropagation()}>
      <button type="button" class={cx('fb-vote-btn', isUp && 'is-up')} aria-pressed={isUp} aria-label={t(isUp ? 'vote_up_remove' : 'vote_up')} onClick={() => click(1)} data-testid="feedbaacks-vote-up">
        <ChevronUp size={16} />
      </button>
      <span class={cx('fb-vote-score', isUp && 'is-up', isDown && 'is-down')} data-testid="feedbaacks-vote-score">{item.votes_score}</span>
      <button type="button" class={cx('fb-vote-btn', isDown && 'is-down')} aria-pressed={isDown} aria-label={t(isDown ? 'vote_down_remove' : 'vote_down')} onClick={() => click(-1)} data-testid="feedbaacks-vote-down">
        <ChevronDown size={16} />
      </button>
    </div>
  )
}
